<template>
  <div
    class="w-full h-full flex flex-col main-page"
    :class="[
      $dm('bg-slate-50', 'dark:bg-slate-900'),
      $dm('text-black-900', 'dark:text-slate-25'),
      {
        'has-toolbar': hasToolbar,
      },
    ]"
    @keydown.esc="closeWindow"
  >
    <div
      class="header-wrap w-full"
      :class="{
        expanded: !isHeaderCollapsed,
        collapsed: isHeaderCollapsed,
      }"
    >
      <div v-if="!hasShowConversationHistory || routeWithoutHeader">
        <chat-header-expanded
          v-if="!isHeaderCollapsed"
          :intro-heading="channelConfig.welcomeTitle"
          :intro-body="channelConfig.welcomeTagline"
          :avatar-url="channelConfig.avatarUrl"
          :show-popout-button="appConfig.showPopoutButton"
        />
        <chat-header
          v-if="isHeaderCollapsed"
          :article-header-title="articleTitle"
          :article-back-action="articleBackAction"
          :title="channelConfig.websiteName"
          :avatar-url="channelConfig.avatarUrl"
          :show-popout-button="appConfig.showPopoutButton"
          :available-agents="availableAgents"
        />
      </div>
      <HeaderActions
        v-else
        :show-popout-button="appConfig.showPopoutButton"
        :is-in-message-screen="this.$route.name === 'messages'"
        :is-in-home-screen="this.$route.name === 'home'"
      />
    </div>

    <div
      v-if="hasLocalStorageError"
      class="fixed top-10 left-0 right-0 z-110 p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full"
    >
      <div class="relative w-full max-w-md max-h-full">
        <div class="relative bg-white rounded-lg shadow dark:bg-gray-700">
          <button class="absolute p-4" @click="hideLocalStorageError">
            <fluent-icon class="mr-1" icon="dismiss" size="16" />
          </button>
          <div class="p-6 text-center">
            <svg
              class="mx-auto mb-4 text-gray-500 w-12 h-12"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <h3
              class="mb-5 text-md font-normal text-gray-500 dark:text-gray-400"
            >
              It seems you have disabled third party cookies in your browser.
              Please enable it to continue.
            </h3>
            <a
              href="https://hoory.com/"
              target="_blank"
              rel="noopener noreferrer"
              class="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center px-5 py-3 text-center mr-2"
            >
              Learn more
            </a>
          </div>
        </div>
      </div>
    </div>

    <banner />
    <audio-error />
    <router-view />
    <toolbar
      v-if="
        hasToolbar &&
          this.$route.name !== 'messages' &&
          this.$route.name !== 'prechat-form'
      "
    />
    <branding
      v-if="this.$route.name !== 'messages'"
      :disable-branding="disableBranding"
    />
  </div>
</template>
<script>
import Banner from '../Banner.vue';
import AudioError from './AudioError.vue';
import Branding from 'shared/components/Branding.vue';
import ChatHeader from '../ChatHeader.vue';
import ChatHeaderExpanded from '../ChatHeaderExpanded.vue';
import HeaderActions from '../HeaderActions.vue';
import configMixin from '../../mixins/configMixin';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import { mapGetters } from 'vuex';
import { IFrameHelper } from 'widget/helpers/utils';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import Toolbar from './Toolbar.vue';

export default {
  components: {
    Banner,
    AudioError,
    Branding,
    ChatHeader,
    ChatHeaderExpanded,
    HeaderActions,
    FluentIcon,
    Toolbar,
  },
  mixins: [configMixin, darkModeMixin],
  data() {
    return {
      showPopoutButton: false,
      showLocalStorageError: true,
      disableBranding: window.hooryWebChannel.disableBranding || false,
    };
  },
  computed: {
    ...mapGetters({
      availableAgents: 'agent/availableAgents',
      appConfig: 'appConfig/getAppConfig',
      isFocused: 'appConfig/getIsFocused',
      portal: 'helpCenter/getPortal',
      headerTitle: 'helpCenter/getHeaderTitle',
    }),
    articleTitle() {
      return this.headerTitle || this.portal.name;
    },
    isHeaderCollapsed() {
      if (!this.hasIntroText) {
        return true;
      }
      return this.$route.name !== 'home';
    },
    routeWithoutHeader() {
      return this.$route.name !== 'home';
    },
    hasToolbar() {
      return !!window.hooryWebChannel.portalId;
    },
    hasIntroText() {
      return (
        this.channelConfig.welcomeTitle || this.channelConfig.welcomeTagline
      );
    },
    hasLocalStorageError() {
      return window.hasLocalStorageError && this.showLocalStorageError;
    },
  },
  methods: {
    closeWindow() {
      IFrameHelper.sendMessage({ event: 'closeWindow' });
    },
    articleBackAction() {
      if (this.$route.name === 'article-category') {
        this.$router.push({ name: 'article-categories' }).catch();
      } else {
        this.$router.push({ name: 'articles' }).catch();
      }
    },
    hideLocalStorageError() {
      this.showLocalStorageError = false;
      window.hasLocalStorageError = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~widget/assets/scss/variables';
@import '~widget/assets/scss/mixins';

.header-wrap {
  flex-shrink: 0;
  transition: max-height 300ms;
  z-index: 99;
  @include shadow-large;

  &.expanded {
    max-height: 16rem;
  }

  &.collapsed {
    max-height: 4.5rem;
  }

  @media only screen and (min-device-width: 320px) and (max-device-width: 667px) {
    border-radius: 0;
  }
}

.white-gradient {
  background: radial-gradient(
    101.25% 101.25% at 49.87% -1.25%,
    #fdfdff 0%,
    #dfe8fa 51.56%,
    #eddde4 100%
  );
}

.main-page {
  &.has-toolbar {
    ::v-deep {
      .light-box-bg {
        padding-bottom: 124px !important;
      }
      .list-wrapper {
        height: calc(100vh - 272px) !important;
      }
    }
  }
}
</style>
